import { supabase } from '@/integrations/supabase/client';
import { SUPABASE_URL } from '@/components/utils/constants';
import { mediaUrls } from '@/types/media.types';

/**
 * Copia dei file di storage collegati alle attività di un itinerario.
 *
 * Serve a ogni operazione che clona un itinerario lato database (duplicazione,
 * salvataggio come template, collegamento di un template a una richiesta): la RPC
 * copia le righe ma non i file, che restano puntati sui path dell'itinerario
 * sorgente. Sta qui, e non dentro `useItineraries`, perché i chiamanti non hanno
 * tutti bisogno di montare quell'hook.
 */

/** Allegato di un'attività così com'è salvato nella colonna JSONB `documentation`. */
interface ItineraryDocument {
  name: string;
  url: string;
}

/** True se l'URL punta al nostro storage Supabase (file interno, quindi copiabile). */
function isInternalUrl(url: string): boolean {
  if (!url) return false;
  if (SUPABASE_URL && url.startsWith(SUPABASE_URL)) return true;
  return /\/storage\/v1\/object\/(?:public|sign)\/p2b\//.test(url);
}

/**
 * Copia media e documentazione delle attività dall'itinerario sorgente a quello
 * appena creato, seguendo la mappa `activity_id_map` restituita dalla RPC.
 *
 * Fallisce in silenzio di proposito: l'itinerario nuovo esiste già e resta usabile
 * anche se i file non vengono clonati, quindi non deve far fallire il flusso chiamante.
 */
export async function copyItineraryStorageFiles(
  oldItineraryId: string,
  newItineraryId: string,
  activityIdMap: Record<string, string>
): Promise<void> {
  try {
    // Attività dell'itinerario ORIGINALE (con media/documentation originali)
    const { data: oldActivities, error: oldError } = await supabase
      .from('itinerary_activities')
      .select('id, media, documentation')
      .eq('itinerary_id', oldItineraryId);

    if (oldError || !oldActivities) {
      return;
    }

    // Solo le attività effettivamente clonate e con almeno un file interno a Supabase
    const activitiesToCopy = oldActivities
      .filter((act) => activityIdMap?.[act.id])
      .map((act) => ({
        oldActivityId: act.id,
        newActivityId: activityIdMap[act.id],
        // La edge function copy-itinerary-files lavora sui path storage: le basta
        // l'URL. mediaUrls() appiattisce sia il formato legacy sia i MediaItem.
        media: mediaUrls(act.media).filter(isInternalUrl),
        // `documentation` arriva come Json grezzo dai tipi generati: la forma reale
        // e' sempre una lista di allegati { name, url }.
        documentation: ((act.documentation ?? []) as unknown as ItineraryDocument[]).filter(
          (doc) => isInternalUrl(doc.url)
        ),
      }))
      .filter((act) => act.media.length > 0 || act.documentation.length > 0);

    if (activitiesToCopy.length === 0) {
      return;
    }

    // Chiamata unica alla edge function
    const { error } = await supabase.functions.invoke('copy-itinerary-files', {
      body: {
        bucket: 'p2b',
        oldItineraryId,
        newItineraryId,
        activities: activitiesToCopy,
      },
    });

    if (error) {
      console.error('Error copying itinerary storage files:', error);
    }
  } catch (error) {
    console.error('Unexpected error copying itinerary storage files:', error);
  }
}
